<template>
    <div class="satisfaction-enquete">
        <div class="header">
            <h1>
                {{ $t('satisfaction.questionnaire.title') }}
                <ReglePastille cle="questionnaireSatisfaction.definition" />
            </h1>
        </div>

        <div v-if="steps.length > 0" class="step-bar-container">
            <div v-for="(step, index) in steps" :key="index" class="step-item">
                <Step :number="index + 1" :state="getStepState(index)" :title="step.label" />
                <div v-if="index < steps.length - 1" class="separator"></div>
            </div>
        </div>
        <div
            v-if="questionData"
            :class="{
                expanded:
                    questionType === 'TEXTAREA' ||
                    questionType === 'CHECKBOX' ||
                    (questionType === 'RADIO' && radioOptions.length > 0),
            }"
            class="question-container"
        >
            <div class="question">
                <p v-if="questionType !== 'CHOIX_SITE_RECLAMATION' || showSiteSelection">{{ questionTitle }}</p>
                <span v-if="questionType === 'TEXTAREA' && characterCount < minCharacters" class="nb-char"
                    >{{ $t('satisfaction.questionnaire.nbcharmin.textarea') }} {{ minCharacters }}
                    {{ $t('satisfaction.questionnaire.nbcharmax.textarea') }}.<br /><br
                /></span>
                <ReglePastille v-if="questionType === 'TEXTAREA'" cle="questionnaireSatisfaction.limitesTexteLibre" />
                <textarea
                    v-if="questionType === 'TEXTAREA'"
                    v-model="response"
                    :placeholder="$t('satisfaction.questionnaire.rep.textarea')"
                    @input="updateCharacterCount"
                ></textarea>
                <div v-if="questionType === 'TEXTAREA'" class="nb-char">
                    <span
                        >{{ characterCount }}/{{ maxCharacters }}
                        {{ $t('satisfaction.questionnaire.nbcharmax.textarea') }}</span
                    >
                </div>

                <div class="checkbox-option">
                    <ReglePastille v-if="questionType === 'CHECKBOX'" cle="questionnaireSatisfaction.optionAutre" />
                    <div v-if="questionType === 'CHECKBOX' && options.length > 0" class="checkbox-group">
                        <div v-for="(option, index) in options1" :key="index" class="checkbox-option">
                            <input
                                :id="'option-' + index"
                                :checked="isChecked(typeof option === 'object' ? option.id : option)"
                                :disabled="isOptionDisabled(option)"
                                :value="typeof option === 'object' ? option.id : option"
                                type="checkbox"
                                @change="toggleCheckbox(typeof option === 'object' ? option.id : option)"
                            />
                            <label :for="'option-' + index">{{ libelleOption(option) }}</label>
                            <input
                                v-if="typeof option === 'object' && option.isChampsLibre && isChecked(option.id)"
                                v-model="otherValue"
                                :placeholder="$t('satisfaction.questionnaire.rep.textautre')"
                                class="other-input"
                                maxlength="350"
                                type="text"
                                @input="updateOtherValueCharCount"
                            />
                            <div
                                v-if="typeof option === 'object' && option.isChampsLibre && isChecked(option.id)"
                                class="char-count"
                            >
                                <span
                                    >{{ otherValueCharCount }}/350
                                    {{ $t('satisfaction.questionnaire.nbcharmax.textarea') }}</span
                                >
                            </div>
                        </div>
                    </div>
                    <div v-if="questionType === 'CHECKBOX' && options.length > 0" class="checkbox-group">
                        <div v-for="(option, index) in options2" :key="index" class="checkbox-option">
                            <input
                                :id="'option2-' + index"
                                :checked="isChecked(typeof option === 'object' ? option.id : option)"
                                :disabled="isOptionDisabled(option)"
                                :value="typeof option === 'object' ? option.id : option"
                                type="checkbox"
                                @change="toggleCheckbox(typeof option === 'object' ? option.id : option)"
                            />
                            <label :for="'option2-' + index">{{ libelleOption(option) }}</label>
                            <input
                                v-if="typeof option === 'object' && option.isChampsLibre && isChecked(option.id)"
                                v-model="otherValue"
                                :placeholder="$t('satisfaction.questionnaire.rep.textautre')"
                                class="other-input"
                                maxlength="350"
                                type="text"
                                @input="updateOtherValueCharCount"
                            />
                            <div
                                v-if="typeof option === 'object' && option.isChampsLibre && isChecked(option.id)"
                                class="char-count"
                            >
                                <span
                                    >{{ otherValueCharCount }}/350
                                    {{ $t('satisfaction.questionnaire.nbcharmax.textarea') }}</span
                                >
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="questionType === 'RADIO' && radioOptions.length > 0" class="radio">
                    <div v-for="(option, index) in radioOptions" :key="index" class="radio-option">
                        <input :id="'option-' + index" v-model="response" :value="option.value" type="radio" />
                        <label :for="'option-' + index">{{ libelleRadio(option) }}</label>
                    </div>
                </div>
            </div>
            <div v-if="questionType === 'CHOIX_SITE_RECLAMATION'">
                <ReglePastille cle="questionnaireSatisfaction.noteInsatisfaitSites" />
                <ChoixSiteSatisfaction
                    v-if="showSiteSelection"
                    :isMultiSite="isMultiSite"
                    :selectedSite="selectedSite"
                    :selectedSites="selectedSites"
                    class="step-one-container"
                    @valider="handleSiteSelection"
                />
                <div v-else>
                    <h3>{{ $t('satisfaction.selected_sites_title') }}</h3>
                    <ul class="vertical-list">
                        <li v-for="site in selectedSites" :key="site.id" class="list-item">
                            {{ site.description }}
                        </li>
                    </ul>
                    <hr v-if="isMultiSite && selectedSites.length >= 1" class="solid" />
                </div>
            </div>
        </div>

        <BottomNavigation
            v-if="!withoutBotNav"
            :back="currentStep > 1 ? prevStep : null"
            :cancel="cancel"
            :confirm="nextStep"
            :confirmButtonDisabled="!activeValidate"
            :first="currentStep === 1"
            :last="currentStep === questions.length"
            :next="nextStep"
        />
        <WaitingModal v-if="loading" :text="$t('intervention.request.pending')" />
        <SuccessModal
            v-if="isSubmitted"
            :optionalImage="optionalImagePath"
            :subtitle="
                note === 1
                    ? $t('satisfaction.questionnaire.remerciement.reclamation.message')
                    : $t('satisfaction.questionnaire.remerciement.message')
            "
            :title="$t('satisfaction.questionnaire.remerciement.title')"
            @cancel="exit"
        />
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import SatisfactionService from '@/modules/Satisfaction/services/SatisfactionService';
import {
    OptionDTO,
    QuestionDTO,
    RechercheQuestionsDTO,
    ReponseSatisfactionDTO,
    SatisfactionDTO
} from '@/models/Satisfaction';
import BottomNavigation from '@/components/layout/BottomNavigation.vue';
import ChoixSiteSatisfaction from '@/modules/Satisfaction/pages/ChoixSiteSatisfaction.vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import { getRadioOptions, RadioOption } from '@/components/ui-components/radioOptions';
import StringUtils from '@/common/utils/StringUtils';
import i18n from '@/plugins/i18n';
import { Site } from '@/models/Site';
import Step, { StepState } from '@/components/ui-components/Step.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import SuccessModal from '@/components/ui-components/SuccessModal.vue';
import enqueteModal from '@/assets/img/finEnqueteSatisfactionModal.png';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import ErrorManager from '@/common/utils/ErrorManager';
import axios from 'axios';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'QuestionPage',
    components: {
        SuccessModal,
        WaitingModal,
        Step,
        BottomNavigation,
        ChoixSiteSatisfaction,
        ReglePastille,
    },
    props: {
        note: {
            type: Number,
            required: true,
        },
        step: {
            type: Number,
            required: true,
        },
        withoutBotNav: {
            type: Boolean,
            required: false,
        },
    },

    setup(props) {
        const router = useRouter();
        const store = useStore();

        const compteId = computed(() => store.getters.getUserInformations.t_compte_id);

        const questions = ref<QuestionDTO[]>([]);
        const steps = ref<{ label: string; completed: boolean }[]>([]);
        const allResponses = ref<{
            [key: string]: ReponseSatisfactionDTO;
        }>({});
        const currentStep = ref(props.step);
        const loading = ref(false);
        const isSubmitted = ref(false);
        const response = ref<string | string[]>('');
        const otherValue = ref('');
        const isOtherSelected = ref(false);
        const prestationIds = ref<number[]>([]);
        const questionData = computed(() => questions.value[currentStep.value - 1] || null);
        const questionTitle = computed(() =>
            questionData.value
                ? StringUtils.traduireOuDefaut(questionData.value.cleI18n, questionData.value.libelle)
                : StringUtils.traduireOuDefaut('satisfaction.questionnaire.question_introuvable', 'Question non trouvée')
        );
        const questionType = computed(() => questionData.value?.type || null);
        // Prestations proposées (questions CHECKBOX)
        const options = computed<OptionDTO[]>(() => {
            if (questionType.value === 'RADIO') {
                return [];
            } else {
                return questionData.value?.options || [];
            }
        });
        // Oui / Non / Ne se prononce pas (questions RADIO)
        const radioOptions = computed<RadioOption[]>(() =>
            questionType.value === 'RADIO' ? getRadioOptions('satisfaction') : []
        );
        const libelleOption = (option: OptionDTO): string => StringUtils.traduireOuDefaut(option.cleI18n, option.libelle);
        // Option « Aucune » : cocher cette option décoche les autres
        const isAucuneOption = (option: OptionDTO | string): boolean => {
            if (typeof option !== 'object') {
                return false;
            }
            return libelleOption(option).trim().toLowerCase() === 'aucune';
        };
        const aucuneOptionId = computed<number | null>(() => {
            const aucune = options.value.find(option => isAucuneOption(option));
            return aucune ? aucune.id : null;
        });
        const isOptionDisabled = (option: OptionDTO | string): boolean =>
            aucuneOptionId.value !== null &&
            !isAucuneOption(option) &&
            isChecked(aucuneOptionId.value.toString());
        const libelleRadio = (option: RadioOption): string => StringUtils.traduireOuDefaut(option.cle, option.value);
        // Libellé d'une étape (« Question 1 »…), traduit si la clé existe dans la langue courante
        const libelleEtape = (numero: number): string =>
            i18n.global.te('satisfaction.questionnaire.etape')
                ? (i18n.global.t('satisfaction.questionnaire.etape', { numero }) as string)
                : `Question ${numero}`;

        const options1 = computed(() => {
            if (questionType.value === 'RADIO') {
                return [];
            } else {
                const halfLength = Math.ceil(questionData.value?.options?.length / 2);
                const liste: OptionDTO[] = [];
                for (let i = 0; i < halfLength; i++) {
                    liste.push(questionData.value?.options[i]);
                }
                return liste || [];
            }
        });

        const options2 = computed(() => {
            if (questionType.value === 'RADIO') {
                return [];
            } else {
                const halfLength = Math.ceil(questionData.value?.options?.length / 2);
                const liste: OptionDTO[] = [];
                for (let i = halfLength; i < questionData.value?.options?.length; i++) {
                    liste.push(questionData.value?.options[i]);
                }
                return liste || [];
            }
        });

        const maxCharacters = computed(() => {
            if (props.note === 1 && questionType.value === 'TEXTAREA') {
                return 1500;
            }
            return 4000;
        });
        const characterCount = computed(() => (typeof response.value === 'string' ? response.value.length : 0));
        const minCharacters = ref(10);

        const otherValueCharCount = computed(() => otherValue.value.length);
        const otherValueMaxChar = ref(350);
        const otherValueMinChar = ref(1);

        const availableSites = computed(() => store.state.siteList.siteList || []);

        const selectedSite = ref<Site | undefined>(undefined);
        const selectedSites = ref<Site[]>([]);
        const isMultiSite = ref(true);
        const showSiteSelection = ref(true);
        const optionalImagePath = ref(enqueteModal);

        const getStepState = (index: number): StepState => {
            if (isSubmitted.value) {
                return StepState.valid;
            }
            if (index < currentStep.value - 1) {
                return StepState.valid;
            } else if (index === currentStep.value - 1) {
                return StepState.active;
            } else {
                return StepState.inactive;
            }
        };

        const updateCharacterCount = () => {
            if (typeof response.value === 'string' && response.value.length > maxCharacters.value) {
                response.value = response.value.slice(0, maxCharacters.value);
            }
        };

        const updateOtherValueCharCount = () => {
            // Interdire l'espace comme premier caractère
            if (otherValue.value.startsWith(' ')) {
                otherValue.value = otherValue.value.trimStart();
            }

            // Limiter à 350 caractères
            if (otherValue.value.length > otherValueMaxChar.value) {
                otherValue.value = otherValue.value.substring(0, otherValueMaxChar.value);
            }
        };

        const handleSiteSelection = (sites: Site[] | Site) => {
            if (Array.isArray(sites)) {
                selectedSites.value = sites;
                isMultiSite.value = true;
            } else {
                selectedSite.value = sites;
                isMultiSite.value = false;
            }
        };

        const editSiteSelection = () => {
            showSiteSelection.value = true;
        };

        const activeValidate = computed(() => {
            if (questionType.value === 'TEXTAREA' && typeof response.value === 'string') {
                return response.value.trim().length >= minCharacters.value;
            } else if (questionType.value === 'CHECKBOX') {
                const isValid = Array.isArray(response.value) && response.value.length > 0;

                // Vérification supplémentaire pour otherValue si une option de type champsLibre est sélectionnée
                const hasLibreOption =
                    Array.isArray(response.value) &&
                    options.value.some(
                        opt => typeof opt === 'object' && opt.isChampsLibre && isChecked(opt.id.toString())
                    );

                if (hasLibreOption) {
                    return isValid && otherValue.value.length >= otherValueMinChar.value;
                }

                return isValid;
            } else if (questionType.value === 'RADIO') {
                return response.value !== '';
            } else if (questionType.value === 'CHOIX_SITE_RECLAMATION') {
                return selectedSites.value.length > 0;
            }
            return false;
        });

        const fetchQuestions = async () => {
            try {
                const params: RechercheQuestionsDTO = { note: props.note };
                questions.value = await SatisfactionService.getQuestions(params);

                steps.value = questions.value.map((_, index) => ({
                    label: libelleEtape(index + 1),
                    completed: index < props.step - 1,
                }));

                setResponseType();
            } catch (error) {
                console.error('Erreur lors de la r�cup�ration des questions :', error);
            }
        };

        const toggleCheckbox = (optionId: number) => {
            if (!Array.isArray(response.value)) {
                response.value = [];
            }
            const option = options.value.find(opt => opt.id === optionId);
            const index = (response.value as string[]).indexOf(optionId.toString());
            if (index > -1) {
                (response.value as string[]).splice(index, 1);
                prestationIds.value.splice(index, 1);
            } else {
                if (option && isAucuneOption(option)) {
                    // Cocher « Aucune » décoche toutes les autres options sélectionnées
                    response.value = [];
                    prestationIds.value = [];
                    otherValue.value = '';
                }
                (response.value as string[]).push(optionId.toString());
                prestationIds.value.push(optionId);
            }
        };

        const isChecked = (optionId: string): boolean => {
            if (Array.isArray(response.value)) {
                return response.value.includes(optionId);
            }
            return false;
        };

        const setResponseType = () => {
            const savedResponse = allResponses.value[questionData.value?.libelle];
            if (savedResponse) {
                // Restaurer la réponse sauvegardée si elle existe
                if (questionType.value === 'CHECKBOX') {
                    response.value = savedResponse.reponse.split(',');
                } else {
                    response.value = savedResponse.reponse;
                }
                otherValue.value =
                    savedResponse.prestations.filter(presta => presta.isChampsLibre)[0]?.reponseAutre || '';
                isOtherSelected.value = savedResponse.isOtherOptionSelected;
            } else {
                // Réinitialiser la réponse en fonction du type de question si aucune réponse n'a été sauvegardée
                response.value =
                    questionType.value === 'CHECKBOX' || questionType.value === 'CHOIX_SITE_RECLAMATION' ? [] : '';
            }
        };

        const saveResponse = () => {
            if (questionData.value) {
                let reponse: string;
                let adresseSiteIds: number[] = [];
                let prestations: OptionDTO[] = [];

                if (questionType.value === 'CHOIX_SITE_RECLAMATION') {
                    adresseSiteIds = selectedSites.value.map(site => site.id);
                    reponse = adresseSiteIds.join(',');
                } else if (Array.isArray(response.value)) {
                    reponse = response.value.join(',');
                    const options: OptionDTO[] = questions.value.filter(question => question.type === 'CHECKBOX')[0]
                        .options;
                    prestations = prestationIds.value.map(id => {
                        return options.filter(option => option.id === id)[0];
                    });
                    if (prestations.filter((option: OptionDTO) => option.isChampsLibre).length !== 0) {
                        prestations.filter((option: OptionDTO) => option.isChampsLibre)[0].reponseAutre =
                            otherValue.value;
                    }
                } else {
                    reponse = response.value as string;
                }

                allResponses.value[questionData.value.libelle] = {
                    questionId: questionData.value.id,
                    questionLabel: questionData.value.libelle,
                    reponse: reponse,
                    reponseFermee: questionType.value !== 'TEXTAREA',
                    dateReponse: new Date().toISOString(),
                    isOtherOptionSelected: isOtherSelected.value || false,
                    otherValue: isOtherSelected.value ? otherValue.value : '',
                    adresseSiteIds: adresseSiteIds,
                    prestations: prestations,
                };
            }
        };

        const prevStep = () => {
            if (questions.value[currentStep.value - 1].type === 'CHOIX_SITE_RECLAMATION' && !showSiteSelection.value) {
                showSiteSelection.value = true;
                return;
            }
            if (currentStep.value > 1) {
                saveResponse();
                currentStep.value--;

                const previousResponse = allResponses.value[questions.value[currentStep.value - 1].libelle];
                if (previousResponse) {
                    if (questions.value[currentStep.value - 1].type === 'CHECKBOX') {
                        response.value = previousResponse.reponse.split(',');
                        otherValue.value =
                            previousResponse.prestations.filter(presta => presta.isChampsLibre)[0]?.reponseAutre || '';
                    } else {
                        response.value = previousResponse.reponse;
                    }
                    isOtherSelected.value = previousResponse.isOtherOptionSelected;
                }

                router.push({ name: 'QuestionPage', params: { note: props.note, step: currentStep.value } });
            }
        };

        const nextStep = () => {
            saveResponse();

            steps.value[currentStep.value - 1].completed = true;

            if (currentStep.value < questions.value.length) {
                currentStep.value++;
                setResponseType();
                isOtherSelected.value = false;
                router.push({ name: 'QuestionPage', params: { note: props.note, step: currentStep.value } });
                const avancement: string = currentStep.value.toString() + '/' + (questions.value.length + 1).toString();
                updateSatisfaction(avancement, Number(questions.value[currentStep.value - 2].id));
            } else {
                submitEnquete();
            }
        };
        const exit = () => {
            router.push('/dashboard');
        };

        const cancel = () => {
            router.push('/dashboard');
        };

        const updateSatisfaction = async (numeroQuestionSaisie: string, questionId: number) => {
            const satisfactionDTO: SatisfactionDTO = store.getters['satisfactionState/getSatisfaction'];
            satisfactionDTO.numeroQuestionSaisie = numeroQuestionSaisie;
            satisfactionDTO.questionIdRepondue = questionId;
            await SatisfactionService.updateSatisfaction(satisfactionDTO);
        };

        const submitEnquete = async () => {
            try {
                saveResponse();
                loading.value = true;
                const responses: ReponseSatisfactionDTO[] = Object.values(allResponses.value);

                const submissionData: {
                    note: number;
                    compteId: any;
                    dateSaisie: string;
                    reponses: ReponseSatisfactionDTO[];
                    societeId: number;
                } = {
                    compteId: compteId.value,
                    note: props.note,
                    dateSaisie: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                    reponses: responses,
                    societeId: store.getters['satisfactionState/getSocieteIdSelectionnee'],
                };

                await SatisfactionService.submitResponses(submissionData);

                isSubmitted.value = true;
            } catch (error: unknown) {
                if (axios.isAxiosError(error)) {
                    const res = error.response;
                    if (res) {
                        store.commit(
                            `errorToaster/${ERROR.SET_MESSAGE}`,
                            await ErrorManager.processErrorCustomMessage(error, 'satisfaction.error.message'),
                            { root: true }
                        );
                    }
                } else {
                    store.commit(
                        `errorToaster/${ERROR.SET_MESSAGE}`,
                        await ErrorManager.processErrorCustomMessage(error, 'app.error.undefined'),
                        { root: true }
                    );
                }
            } finally {
                loading.value = false;
            }
        };

        onMounted(async () => {
            await fetchQuestions();
        });

        watch([questionData, availableSites], () => {
            if (questionType.value === 'CHOIX_SITE_RECLAMATION' && availableSites.value.length > 0) {
                if (availableSites.value.length === 1) {
                    // S'il n'y a qu'un seul site disponible
                    selectedSites.value = [...availableSites.value];
                    isMultiSite.value = false;
                    showSiteSelection.value = false;
                } else {
                    // S'il y a plusieurs sites disponibles
                    isMultiSite.value = true;
                    showSiteSelection.value = true;
                }
            }
        });

        return {
            getStepState,
            questionData,
            questionTitle,
            questionType,
            options,
            options1,
            options2,
            radioOptions,
            libelleOption,
            libelleRadio,
            response,
            toggleCheckbox,
            isChecked,
            isOptionDisabled,
            nextStep,
            prevStep,
            cancel,
            confirm,
            currentStep,
            steps,
            loading,
            isSubmitted,
            exit,
            questions,
            allResponses,
            activeValidate,
            minCharacters,
            maxCharacters,
            characterCount,
            updateCharacterCount,
            otherValue,
            isOtherSelected,
            otherValueCharCount,
            otherValueMaxChar,
            otherValueMinChar,
            updateOtherValueCharCount,
            selectedSite,
            selectedSites,
            isMultiSite,
            showSiteSelection,
            editSiteSelection,
            handleSiteSelection,
            optionalImagePath,
        };
    },
});
</script>

<style scoped>
:deep(.button-container) {
    margin-left: 80px;
}
.step-bar-container {
    display: flex;
    justify-content: space-between;
    width: calc(100% - 48px);
    background-color: #fff;
    height: 28px;
    align-items: center;
    padding: 16px 24px;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12);
}

.step-item {
    display: flex;
    align-items: center;
    width: calc(100% - 48px);
    justify-content: space-around;
}

.separator {
    height: 1px;
    background-color: var(--medium-light-grey);
    flex-shrink: 1;
    min-width: 21px;
}

.satisfaction-enquete {
    width: calc(100% - 48px);
    margin: 0 auto;
    padding: 0;
    font-family: 'Roboto', sans-serif;
}

.header {
    padding: 8px 8px;
}

.header h1 {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin: 0;
}

.question-container {
    padding: 8px 40px;
    margin-top: 4px;
    background-color: #ffffff;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    height: calc(100vh - 250px);
    transition: height 0.3s ease;
    overflow-y: auto;
}

.question-container.expanded {
    height: 51vh;
    min-height: 40vh;
}
@media (max-height: 696px) {
    .question-container.expanded {
        height: 58vh;
        min-height: 40vh;
    }
}
.question p {
    font-size: 18px;
    margin-bottom: 16px;
    color: #495057;
    font-family: 'Roboto', sans-serif;
}

.nb-char {
    font-size: 14px;
    margin: 15px 0 15px 0;
    color: #495057;
    font-family: 'Roboto', sans-serif;
}

textarea {
    width: 98%;
    height: 200px;
    padding: 15px;
    font-family: 'Raleway', sans-serif;
    font-variant-numeric: lining-nums;
    color: var(--black);
    top: 8px;
    border: 1px solid var(--medium-dark-grey);
    border-radius: 4px;
    resize: none;
    outline: none;
    transition: border-color 0.3s ease;
}

textarea:focus {
    border-color: #5298d2;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
}

.checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 50%;
}

.checkbox-option {
    display: flex;
    align-items: center;
}

.checkbox-option input[type='checkbox'] {
    margin-right: 10px;
    width: 20px;
    height: 20px;
}

.checkbox-option input[type='checkbox']:disabled {
    cursor: not-allowed;
}

.checkbox-option input[type='checkbox']:disabled + label {
    color: var(--medium-dark-grey);
}

.question-container .radio-option {
    margin-top: 16px;
    padding-bottom: 16px;
}

.radio {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.char-count {
    font-size: 12px;
    margin-top: 4px;
    margin-left: 30px;
    color: #495057;
    font-family: 'Roboto', sans-serif;
}

.other-input {
    margin-left: 10px;
    width: 300px;
    border: 1px solid var(--medium-dark-grey);
    border-radius: 4px;
    padding: 5px;
    transition: border-color 0.3s ease;
}

.other-input:focus {
    border-color: #5298d2;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
}

.confirmation h2 {
    font-size: 24px;
    margin-bottom: 20px;
    color: #4caf50;
}

.confirmation p {
    font-size: 16px;
    color: #555;
}

hr.solid {
    border-top: 3px solid #bbb;
}

.btn {
    width: 90px;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin: 5px;
}

.button-modal-container {
    text-align: right;
}

.vertical-list {
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
    list-style-type: circle;
}

@media (min-height: 776px) {
    .question-container.expanded {
        height: 65vh;
        max-height: 65vh;
        min-height: 55vh;
    }
}
</style>
